import { BaseRecord } from '@refinedev/core';
import { create } from 'zustand';

export interface ExperienceStoreConfig {
  storageKey: string;
}

export interface ExperienceState extends BaseRecord {
  experience: any;
  experienceViewMode: string | null;
  showPreviewSuccess: boolean;
  showPdfCompare: boolean;
  activeExperienceId: string | null;
  loading: boolean;
  error: null;
  experienceConfig: any;
  monitorConfig: {
    sortType: string;
  };
  exportedData: any;
}

interface ExperienceActions {
  setExperience: (state: any) => void;
  setActiveExperienceId: (id: string | null) => void;
  setShowPreviewSuccess: (value: boolean) => void;
  setShowPdfCompare: (value: boolean) => void;
  setExperienceViewMode: (value: string | null) => void;
  setMonitorConfig: (config: { sortType: string }) => void;
  setExportedData: (key: string, data: any) => void;
}

type ExperienceStore = ExperienceState & ExperienceActions & { config: ExperienceStoreConfig };

export const experienceStoreConfig: ExperienceStoreConfig = {
  storageKey: 'experienceState',
};

const loadedExperienceConfig = {
  isMBTask: true,
  isPamojaTask: true,
  mbGradeBookEmbeddedView: true,
  pageHeader: {
    back: {
      show: true,
    },
    title: {
      show: true,
      inlineEdit: true,
    },
    subtitle: {
      show: true,
    },

    resource: {
      show: true, // based on manage.canEdit below
    },
    preview: {
      show: true,
    },
    rearrange: {
      show: true,
    },
    changeStatus: {
      show: true,
    },
    joinCode: {
      show: true,
    },
    duplicate: {
      show: true,
    },
    points: {
      show: true,
    },
    criteria: {
      show: true,
    },
    deliveryMode: {
      show: true,
    },
    selectInvigilators: {
      show: true,
    },
    selectCollaborators: {
      show: true,
    },
    progressBar: {
      show: true,
    },
    instructions: {
      show: true,
    },
    questionsCount: {
      show: true,
    },
    userName: {
      show: true,
    },
    endTest: {
      show: true,
      disabled: true,
    },
    timer: {
      show: false,
    },
    calculator: {
      enabled: true,
    },
    qrModal: {
      show: true,
    },
    bookmark: {
      show: true,
      disabled: true,
    },
    video: {
      apVideoMonitoring: {
        enabled: true,
        // jitsiConfig: getJitsiConfig,
      },
      externalLink: {
        show: true,
        openInNewTab: true,
      },
    },
    mobileUploadDone: {
      show: true,
    },
  },
  manage: {
    canEdit: true,
    create: {
      show: true,
      alerts: {
        published: {
          show: true,
        },
        closed: {
          show: true,
        },
      },
      topics: {
        layout: {
          span: 4,
          offset: 1,
        },
        list: {
          show: true,
        },
        create: {
          showFirst: true,
          show: true,
          disabled: false,
        },
        edit: {
          enabled: true,
          disabled: false,
        },
        delete: {
          enabled: true,
          disabled: false,
        },
      },
      segments: {
        layout: {
          span: 18,
          offset: 1,
        },
        list: {
          show: true,
        },
        create: {
          showFirst: true,
          show: true,
          disabled: false,
          content: {
            enabled: true,
          },
          questions: {
            enabled: true,
          },
          games: {
            enabled: false,
          },
          addFromLibrary: {
            enabled: true,
          },
        },
        edit: {
          enabled: true,
          disabled: false,
        },
        delete: {
          enabled: true,
          disabled: false,
        },
      },
    },
    deliver: {
      show: true,
      alerts: {
        draft: {
          show: true,
        },
        published: {
          show: true,
        },
        closed: {
          show: true,
        },
      },
      settings: {
        show: true,
        mode: {
          show: true,
          disabled: true,
        },
      },
      studentMemberships: {
        show: true,
        disabled: true,
        allowGuests: {
          show: true,
        },
        mappedStudents: {
          show: true,
          disabled: true,
          allowOnline: true,
          selectable: true,
        },
      },
    },
    monitor: {
      show: true,
      realtime: {
        enabled: true,
      },
      liveResponses: {
        // teacher can choose to see one particular live response
        enabled: true,
      },
      liveQuiz: {
        enabled: true, // also in take - SID in sync between teacher and student - teacher controls - one at a time
      },
      filters: {
        show: true,
        search: {
          show: true,
        },
        group: {
          show: true,
        },
      },
      addTime: {
        show: true,
      },
      timer: {
        show: true,
      },
      apVideoMonitoring: {
        show: true,
      },
      studentList: {
        show: true,
      },
    },
    grade: {
      show: true,
    },
  },
  takeExperience: {
    firstView: 'joined',
    // separate pageHeader here OR use above and have role/experienceViewMode conditions there

    focusLost: {
      enabled: true,
    },
    nativeSecurity: {
      // @uttam @abhimanyu for example this can be changed from monitor page - so a watcher for userInfoFromReduxNativeSecurity will set this as false? obv set this later inside the take component, only default here false
      // enabled:
      // currentUser.role === "student" &&
      // (appType === "mac" || appType === "ios") &&
      // experienceSettings.mode === "offline"
      //   ? true
      //   : false,
    },
    video: {
      apVideoMonitoring: {
        enabled: true,
      },
      externalLink: {
        show: true,
        openInNewTab: true,
      },
    },
    spellcheck: {
      enabled: false,
    },
    calculator: {
      enabled: false,
    },
    gamification: {
      enabled: false, // first topic open and earn points to unlock next topics
      leaderboard: {
        show: false,
      },
    },
    engagement: {
      enabled: false,
      video: {
        enabled: false,
        points: 100,
      },
      question: {
        enabled: true,
        points: 20,
      },
      game: {
        enabled: true,
        points: 50,
      },
    },
    segmentFlow: {
      oneByOne: false, // for livequizzes/ slides creation/take one by one
    },
    liveQuiz: {
      enabled: false, // means that SID with live firestore connection to teacher monitor - segment that student sees controlled by teacher. teacher sees all responses/graph in realtime
    },
    join: {
      createExperienceUserOnStart: false,
      startTime: {
        show: true,
        timer: true,
      },
      startTestBtn: {
        // button
        show: true,
        disabled: true,
      },
      quitTestBtn: {
        // button
        show: true,
      },
      backToHomeBtn: {
        show: true,
      },
    },
    start: {
      video: {
        show: true,
      },
      // TODO: NEED to separate segmentConfigFromRedux - embed case
      segment: {
        points: {
          show: true,
        },
        ePoints: {
          show: false,
        },
      },
      userResponseSave: {
        showSavingStatusIndicator: true,
        cloud: {
          enabled: true,
        },
        local: {
          enabled: true,
        },
        redux: {
          enabled: true,
        },
        apDb: {
          enabled: false,
        },
      },
      chat: {
        show: true,
      },
      upload: {
        enabled: true, // from setting and mode and appType - override in segmentConfig later as needed
      },
      pageHeader: {
        endButton: {
          show: true,
        },
      },
    },
    resume: {
      requestResume: {
        show: true,
      },
    },
    end: {
      timer: {
        show: true,
      },
      progressBar: {
        show: true,
        attemptedQuestions: {
          show: true,
          wide: true,
        },
      },
      uploadSummary: {
        show: true,
      },
      quitTest: {
        // button
        show: true,
      },
      backToStart: {
        show: true,
      },
      video: {
        apVideoMonitoring: {
          enabled: true,
        },
      },
      requestResume: {
        show: true,
      },
    },
    submit: {
      submitTest: {
        show: true,
        disabled: true,
      },
      quitTest: {
        // button
        show: true,
      },
      backToHome: {
        show: true,
      },
      viewResult: {
        show: false,
      },
    },
    result: {
      quitTest: {
        // button
        show: true,
      },
    },
  },
};

const initialState: ExperienceState = {
  experience: null,
  activeExperienceId: null,
  showPreviewSuccess: false,
  showPdfCompare: false,
  experienceViewMode: null,
  loading: false,
  error: null,
  experienceConfig: loadedExperienceConfig,
  monitorConfig: {
    sortType: 'smart_sort',
  },
  exportedData: {},
};

export const useExperienceStore = create<ExperienceStore>((set) => ({
  ...initialState,
  config: experienceStoreConfig,
  setExperience: (experience: any) =>
    set((state) => {
      if (!experience) return { experience, activeExperienceId: null };
      // Only experiences#show returns `room_inviligator_membership`; update responses
      // omit it, so keep the existing value when updating the same experience.
      const current = state.experience;
      const isSameExperience = current?.id != null && experience.id === current.id;
      return {
        experience: {
          ...experience,
          room_inviligator_membership:
            experience.room_inviligator_membership ??
            (isSameExperience ? current?.room_inviligator_membership : undefined),
        },
        activeExperienceId: experience.id,
      };
    }),
  setExperienceConfig: (config: any) => set({ experienceConfig: config }),
  setActiveExperienceId: (id: string | null) => set({ activeExperienceId: id }),
  setShowPreviewSuccess: (value: boolean) => {
    set({ showPreviewSuccess: value });
  },
  setShowPdfCompare: (value: boolean) => set({ showPdfCompare: value }),
  setExperienceViewMode: (value: string | null) => set({ experienceViewMode: value }),

  setMonitorConfig: (config: { sortType: string }) =>
    set((state) => ({
      monitorConfig: {
        ...state.monitorConfig,
        ...config,
      },
    })),

  setExportedData: (key: string, data: any) =>
    set((state) => ({
      exportedData: { ...state.exportedData, [key]: data },
    })),
}));

export const selectExperience = (state: ExperienceStore): any => state.experience;

export const selectActiveExperienceId = (state: ExperienceStore): any => state.activeExperienceId;

export const selectExperienceViewMode = (state: ExperienceStore): any => state.experienceViewMode;

export const selectShowPdfCompare = (state: ExperienceStore): boolean => state.showPdfCompare;

export const selectExperienceConfig = (state: ExperienceStore): any => state.experienceConfig;

export const selectExperienceRubric = (state: ExperienceStore): any => state.experience?.rubric;

export const selectExperienceRubricType = (state: ExperienceStore): any =>
  state.experience?.rubric?.type_c;

export const selectExperienceSettings = (state: ExperienceStore): any => state.experience?.settings;

export const selectExperienceSubjectItemLabel = (state: ExperienceStore): string | undefined =>
  state.experience?.custom_fields?.subject_item_label;

export const selectAttemptableQuestionsCount = (state: ExperienceStore): number | undefined =>
  state.experience?.attemptable_questions_count;

export const selectExperienceMappedStudentsCount = (state: ExperienceStore): any =>
  state.experience?.students?.length || 0;

export const selectMonitorConfig = (state: ExperienceStore): any => state.monitorConfig;

export const selectExportedData = (
  state: ExperienceStore,
  exportType: any,
  exportModalConfig: any
): any => {
  return state.exportedData[exportType];
  // Fallback to config/experience data
  // return exportType === 'student_submission'
  //   ? exportModalConfig?.experienceUser?.export_json?.[exportType]
  //   : state.experience?.export_json?.[exportType];
};
